/* OceanWash pan set: one of each pan, one gift per order, existing cart API/events. */
(() => {
  'use strict';
  if (window.owPanBundle) return;
  const configNode = document.querySelector('[data-ow-pan-bundle-config]');
  if (!configNode) return;
  const cfg = JSON.parse(configNode.textContent);
  const root = (cfg.root || '/').replace(/\/?$/, '/');
  const ids = [String(cfg.pan24), String(cfg.pan28)];
  const giftId = String(cfg.gift);
  // The former gift-code setting is retained only to clear it from existing carts.
  // Gift eligibility and pricing are now enforced by the automatic server discount.
  const code = String(cfg.code || '').toUpperCase();
  let pending = Promise.resolve();
  let refreshing = false;
  let offerInCart = !!cfg.discountedGiftInCart || !!document.querySelector('[data-ow-pan-cart-status]');

  async function request(path, body) {
    const response = await fetch(root + path, body ? {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    } : { cache: 'no-store' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || cfg.error);
    return data;
  }
  const quantity = (cart, id) => cart.items.reduce((sum, item) => sum + (String(item.variant_id) === id ? item.quantity : 0), 0);
  const eligible = cart => ids.every(id => quantity(cart, id) > 0);
  const markedGift = item => String(item.variant_id) === giftId && item.properties && item.properties._ow_pan_gift === '1';
  const hasOurCode = cart => !!code && (cart.discount_codes || []).some(entry => String(entry.code).toUpperCase() === code);
  const needsOfferGuard = cart => hasOurCode(cart) || cart.items.some(item => ids.includes(String(item.variant_id)) || markedGift(item));

  async function refresh(cart) {
    if (typeof window.refreshCartContents !== 'function') return;
    refreshing = true;
    try {
      // Ecomify expects a Response, not already-parsed JSON.
      await window.refreshCartContents(new Response(JSON.stringify(cart), { status: 200, headers: { 'Content-Type': 'application/json' } }));
    } finally { refreshing = false; }
  }
  function serialize(work) {
    const task = pending.catch(() => {}).then(work);
    pending = task;
    return task;
  }
  function showError(message) {
    document.querySelectorAll('[data-ow-pan-cart-error]').forEach(node => {
      node.textContent = message || cfg.error;
      node.hidden = false;
    });
  }

  async function synchronize(cart) {
    let changed = false;
    // Keep code-only carts guarded until their removal request has completed.
    offerInCart = needsOfferGuard(cart);
    const gifts = cart.items.filter(markedGift);
    const keepGift = eligible(cart) ? 1 : 0;
    const updates = {};
    let remaining = keepGift;
    gifts.forEach(item => {
      const keep = Math.min(item.quantity, remaining);
      remaining -= keep;
      if (item.quantity !== keep) updates[item.key] = keep;
    });
    if (Object.keys(updates).length) {
      cart = await request('cart/update.js', { updates });
      changed = true;
    }
    const hasEligibleGift = eligible(cart) && cart.items.some(markedGift);
    const codes = (cart.discount_codes || []).map(entry => entry.code);
    if (hasOurCode(cart)) {
      const nextCodes = codes.filter(entry => entry.toUpperCase() !== code);
      cart = await request('cart/update.js', { discount: nextCodes.join(',') });
      changed = true;
    }
    if (hasEligibleGift) {
      const gift = cart.items.find(markedGift);
      const giftSavings = cart.items.reduce((sum, item) => sum + (String(item.variant_id) === giftId ? item.original_line_price - item.final_line_price : 0), 0);
      if (giftSavings < gift.original_price) throw new Error(cfg.error);
    }
    offerInCart = needsOfferGuard(cart);
    return { cart, changed };
  }

  async function addSet(upgrade) {
    offerInCart = true;
    if (typeof window.owDkGratisSync === 'function') await window.owDkGratisSync();
    let cart = await request('cart.js');
    const items = [];
    ids.forEach(id => {
      if (!upgrade || quantity(cart, id) === 0) items.push({ id: Number(id), quantity: 1 });
    });
    if (!cart.items.some(markedGift)) {
      items.push({ id: Number(giftId), quantity: 1, properties: { _ow_pan_gift: '1' } });
    }
    if (items.length) await request('cart/add.js', { items });
    cart = await request('cart.js');
    const result = await synchronize(cart);
    await refresh(result.cart);
    return result.cart;
  }

  window.owPanBundle = {
    handlesCheckout: () => offerInCart || !!document.querySelector('[data-ow-pan-cart-status]'),
    addSet: () => serialize(() => addSet(false)),
    upgrade: () => serialize(() => addSet(true)),
    sync: () => serialize(async () => {
      if (typeof window.owDkGratisSync === 'function') await window.owDkGratisSync();
      const result = await synchronize(await request('cart.js'));
      if (result.changed) await refresh(result.cart);
      return result.cart;
    })
  };
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-ow-pan-upgrade]');
    if (!button || button.disabled) return;
    event.preventDefault();
    document.querySelectorAll('[data-ow-pan-upgrade]').forEach(node => { node.disabled = true; });
    try { await window.owPanBundle.upgrade(); }
    catch (error) { showError(error.message); }
    finally { document.querySelectorAll('[data-ow-pan-upgrade]').forEach(node => { node.disabled = false; }); }
  });
  // Wait for gift reconciliation before the merchant's native checkout click handler.
  document.addEventListener('click', event => {
    const button = event.target.closest('[name="checkout"], .btn-checkout');
    if (!button || button.dataset.owPanReady === '1' || !window.owPanBundle.handlesCheckout()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.owPanBundle.sync().then(() => {
      // A reconciliation may have replaced the cart DOM.
      const current = button.isConnected ? button : document.querySelector('[name="checkout"], .btn-checkout');
      if (!current) return;
      current.dataset.owPanReady = '1';
      current.click();
      delete current.dataset.owPanReady;
    }).catch(error => showError(error.message));
  }, true);
  window.addEventListener('updated.ecomify.cart', () => {
    if (!refreshing) window.owPanBundle.sync().catch(error => showError(error.message));
  });
  window.owPanBundle.sync().catch(error => showError(error.message));
})();
